import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    billingStatus as getBillingStatus,
    BillingStatusData,
    cancelSubscription,
    CancelSubscriptionData,
    createCheckout,
    CreateCheckoutData,
    getPaymentInformation,
    GetPaymentInformationData,
    invoicePdf,
    InvoicePdfData,
    listInvoices,
    ListInvoicesData,
    syncCheckout,
    SyncCheckoutData,
    updatePaymentInformation,
    UpdatePaymentInformationData,
} from "@/api";
import { appState } from "@/app/appState";
import { organisation_tree_node } from "@/app/typing/organisations";
import { Request } from "@/components/CRUD";
import { ActionHandler } from "@/components/UI/tools";
import { Field, Pill, Row, Section, TextField } from "./ui";

const SUBSCRIPTION_TONE = {
    active: "good",
    past_due: "warn",
    cancelled: "danger",
} as const;

type ContactFields = {
    street: string;
    postalCode: string;
    city: string;
    country: string;
    cvr: string;
};

const EMPTY_CONTACT: ContactFields = { street: "", postalCode: "", city: "", country: "", cvr: "" };

const contactFromPayment = (payment: any): ContactFields => {
    const address = payment?.billing_address ?? {};
    const text = (value: unknown) => (typeof value === "string" ? value : "");
    return {
        street: text(address.street),
        postalCode: text(address.postal_code),
        city: text(address.city),
        country: text(address.country),
        cvr: text(payment?.billing_cvr),
    };
};

const CONTACT_FIELD_KEYS = Object.keys(EMPTY_CONTACT) as (keyof ContactFields)[];
const OPTIONAL_CONTACT_FIELDS: (keyof ContactFields)[] = ["cvr"];

const BillingContact = observer(({
    orgId,
    canEdit,
    onChanged,
}: {
    orgId: number;
    canEdit: boolean;
    onChanged: () => void;
}) => {
    const [info, setInfo] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState<ContactFields>(EMPTY_CONTACT);
    const [contact, setContact] = useState<ContactFields>(EMPTY_CONTACT);

    const set = (key: keyof ContactFields) => (value: string) =>
        setContact((current) => ({ ...current, [key]: value }));

    const dirty = CONTACT_FIELD_KEYS.some((key) => contact[key].trim() !== saved[key].trim());
    const hasValidContact =
        CONTACT_FIELD_KEYS.every((key) => OPTIONAL_CONTACT_FIELDS.includes(key) || contact[key].trim().length > 0);

    const load = Request<GetPaymentInformationData, any>(
        getPaymentInformation,
        () => ({ body: { id: orgId } }),
        setLoading,
        (data: any) => {
            const payment = data?.payment_information;
            const stored = contactFromPayment(payment);
            setInfo(payment);
            setSaved(stored);
            setContact(stored);
        },
        () => setInfo(null),
        () => orgId > 0,
    );

    const save = Request<UpdatePaymentInformationData, any>(
        updatePaymentInformation,
        () => ({
            body: {
                id: orgId,
                billing_address: {
                    street: contact.street.trim(),
                    postal_code: contact.postalCode.trim(),
                    city: contact.city.trim(),
                    country: contact.country.trim(),
                },
                billing_cvr: contact.cvr.trim() || null,
            },
        }),
        setLoading,
        () => {
            load();
            onChanged();
        },
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? appState.t("billing.saveContactError"),
            }),
        () => canEdit && dirty && hasValidContact,
    );

    useEffect(() => {
        setSaved(EMPTY_CONTACT);
        setContact(EMPTY_CONTACT);
        load();
    }, [orgId]);

    return (
        <div className="row-form">
            <small className="caption">{appState.t("billing.contact")}</small>

            {info?.card_label && info?.card_last_four && (
                <p className="detail-text">{`${info.card_label} •••• ${info.card_last_four}`}</p>
            )}

            {info?.invoice_recipient?.email && (
                <p className="detail-text">
                    {appState.t("billing.invoiceRecipient", {
                        name: info.invoice_recipient.name || info.invoice_recipient.email,
                        email: info.invoice_recipient.email,
                    })}
                </p>
            )}

            <div className="flex flex-row gap-[.5rem] flex-wrap">
                <Field label={appState.t("billing.street")}>
                    <TextField value={contact.street} onChange={set("street")} placeholder="—" />
                </Field>
                <Field label={appState.t("billing.postalCode")}>
                    <TextField value={contact.postalCode} onChange={set("postalCode")} placeholder="—" />
                </Field>
            </div>
            <div className="flex flex-row gap-[.5rem] flex-wrap">
                <Field label={appState.t("billing.city")}>
                    <TextField value={contact.city} onChange={set("city")} placeholder="—" />
                </Field>
                <Field label={appState.t("billing.country")}>
                    <TextField value={contact.country} onChange={set("country")} placeholder="—" />
                </Field>
            </div>
            <div className="flex flex-row gap-[.5rem] flex-wrap">
                <Field label={appState.t("billing.cvr")}>
                    <TextField value={contact.cvr} onChange={set("cvr")} placeholder="—" />
                </Field>
            </div>

            <div className="flex flex-row items-center justify-between gap-[.5rem]">
                <p className="error-text">
                    {dirty && !hasValidContact ? appState.t("billing.contactRequired") : ""}
                </p>
                <ActionHandler
                    title={appState.t("common.save")}
                    funq={() => save()}
                    disabled={loading || !dirty || !hasValidContact}
                >
                    {appState.t("common.save")}
                </ActionHandler>
            </div>
        </div>
    );
});

const SUBSCRIPTION_STATUS_KEYS: Record<string, string> = {
    none: "billing.status.none",
    active: "billing.status.active",
    past_due: "billing.status.pastDue",
    cancelled: "billing.status.cancelled",
};

const Subscription = observer(({ orgId, canEdit, prominent = false }: { orgId: number; canEdit: boolean; prominent?: boolean }) => {
    const [status, setStatus] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    const load = Request<BillingStatusData, any>(
        getBillingStatus,
        () => ({ body: { id: orgId } }),
        setLoading,
        (data) => setStatus(data),
        () => setStatus(null),
        () => orgId > 0,
    );

    // Picks up a paid checkout whose webhook never landed.
    const sync = Request<SyncCheckoutData, any>(
        syncCheckout,
        () => ({ body: { id: orgId } }),
        setLoading,
        (data) => setStatus(data),
        () => load(),
        () => orgId > 0 && canEdit,
    );

    const checkout = Request<CreateCheckoutData, any>(
        createCheckout,
        () => ({ body: { id: orgId } }),
        setLoading,
        (data: any) => {
            if (data?.checkout_url) window.location.href = data.checkout_url;
        },
        (error) =>
            appState.setModal({
                title: appState.t("billing.checkoutError"),
                body: error.detail?.toString() ?? appState.t("common.somethingWentWrong"),
            }),
        () => canEdit,
    );

    const cancel = Request<CancelSubscriptionData, any>(
        cancelSubscription,
        () => ({ body: { id: orgId } }),
        setLoading,
        () => load(),
        (error) =>
            appState.setModal({
                title: appState.t("billing.cancelError"),
                body: error.detail?.toString() ?? appState.t("common.somethingWentWrong"),
            }),
        () => canEdit,
    );

    useEffect(() => {
        if (canEdit) sync();
        else load();
    }, [orgId, canEdit]);

    const subscriptionStatus: string = status?.subscription_status ?? "none";
    const subscribed = subscriptionStatus === "active" || subscriptionStatus === "past_due";
    const outstandingUnits: number = status?.outstanding_units ?? 0;

    return (
        <>
            <Row
                trailing={
                    <Pill tone={(SUBSCRIPTION_TONE as any)[subscriptionStatus] ?? "neutral"}>
                        {appState.t(SUBSCRIPTION_STATUS_KEYS[subscriptionStatus] ?? SUBSCRIPTION_STATUS_KEYS.none)}
                    </Pill>
                }
            >
                <p className="l">{appState.t("billing.subscription")}</p>
                <p className="detail-text">
                    {status?.paid_until
                        ? appState.t("billing.paidUntil", { date: new Date(status.paid_until).toLocaleDateString(appState.locale) })
                        : appState.t("billing.standardPrice")}
                </p>
            </Row>

            {canEdit && !subscribed && outstandingUnits > 0 && (
                <Row trailing={<Pill tone="warn">{`${status?.outstanding_amount} DKK`}</Pill>}>
                    <p className="detail-text-strong">
                        {appState.t("billing.outstandingUsage", {
                            n: outstandingUnits,
                            amount: status?.outstanding_amount ?? "",
                        })}
                    </p>
                </Row>
            )}

            {canEdit && (
                <div className={`row-actions ${prominent && subscriptionStatus !== "active" ? "row-actions-block" : ""}`}>
                    {subscriptionStatus === "active" ? (
                        <ActionHandler
                            title={appState.t("billing.cancel")}
                            funq={() =>
                                appState.setModal({
                                    title: appState.t("billing.cancelConfirm"),
                                    body: appState.t("billing.cancelBody"),
                                    action: () => cancel(),
                                })
                            }
                            disabled={loading}
                            className="btn-danger"
                        >
                            {appState.t("billing.cancel")}
                        </ActionHandler>
                    ) : (
                        <ActionHandler
                            title={subscriptionStatus === "cancelled" ? appState.t("billing.resubscribe") : appState.t("billing.subscribe")}
                            funq={() => checkout()}
                            disabled={loading}
                            glow={prominent}
                            className={prominent ? "btn-block" : undefined}
                        >
                            {subscriptionStatus === "cancelled" ? appState.t("billing.resubscribePrice") : appState.t("billing.subscribePrice")}
                        </ActionHandler>
                    )}
                </div>
            )}

            {subscribed && (
                <>
                    <Row
                        trailing={<Pill>{appState.t("billing.used", { n: status?.monthly_consumed ?? 0 })}</Pill>}
                    >
                        <p className="l">{appState.t("billing.usageBilling")}</p>
                        <p className="detail-text">{appState.t("billing.usageBillingHint")}</p>
                    </Row>
                </>
            )}
        </>
    );
});

type InvoiceSummary = {
    id: number;
    invoice_number: string;
    kind: "invoice" | "credit_note";
    issued_at: string;
    total: string;
    currency: string;
    status: "issued" | "credited";
};

const Invoices = observer(({ orgId }: { orgId: number }) => {
    const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
    const [loading, setLoading] = useState(false);

    const load = Request<ListInvoicesData, any>(
        listInvoices,
        () => ({ body: { id: orgId } }),
        setLoading,
        (data: any) => setInvoices(data?.invoices ?? []),
        () => setInvoices([]),
        () => orgId > 0,
    );

    const download = Request<InvoicePdfData, any, InvoiceSummary>(
        invoicePdf,
        (invoice) => ({ body: { id: orgId, invoice_id: invoice.id } }),
        setLoading,
        (pdf: Blob, invoice) => {
            const url = URL.createObjectURL(pdf);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${invoice.invoice_number}.pdf`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        () =>
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("billing.invoice.downloadError"),
            }),
        () => !loading,
    );

    useEffect(() => {
        setInvoices([]);
        load();
    }, [orgId]);

    if (invoices.length === 0) return null;

    return (
        <div className="flex flex-col py-[.55rem]">
            <small className="caption px-1 pb-[.25rem]">{appState.t("billing.invoices")}</small>
            {invoices.map((invoice) => (
                <Row
                    key={invoice.id}
                    onClick={() => download(invoice)}
                    disabled={loading}
                    trailing={
                        <div className="flex flex-row items-center gap-[.35rem]">
                            {invoice.kind === "credit_note" && <Pill tone="warn">{appState.t("billing.invoice.creditNote")}</Pill>}
                            <p className="detail-text-strong whitespace-nowrap">
                                {`${Number(invoice.total).toLocaleString(appState.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${invoice.currency}`}
                            </p>
                        </div>
                    }
                >
                    <p className="l">{invoice.invoice_number}</p>
                    <p className="detail-text">
                        {`${new Date(invoice.issued_at).toLocaleDateString(appState.locale)} · ${appState.t("billing.invoice.download")}`}
                    </p>
                </Row>
            ))}
        </div>
    );
});

/**
 * Billing has three honest states: this organisation holds the payment, it is
 * covered by an ancestor, or nothing on this path pays yet. A missing payment
 * record on a child is not the same as being unbilled, so we never draw a local
 * payment editor for an inherited plan.
 */
export const BillingSection = observer(({
    node,
    holder,
    canEdit,
    onSelectOrg,
    onChanged,
}: {
    node: organisation_tree_node;
    holder?: organisation_tree_node;
    canEdit: boolean;
    onSelectOrg: (id: number) => void;
    onChanged: () => void;
}) => {
    if (node.has_payment) {
        return (
            <Section title={appState.t("billing.title")} hint={appState.t("billing.managedHere")}>
                <Subscription orgId={node.id} canEdit={canEdit} />
                {canEdit && <BillingContact orgId={node.id} canEdit onChanged={onChanged} />}
                {canEdit && <Invoices orgId={node.id} />}
            </Section>
        );
    }

    if (node.billing_organisation_id) {
        return (
            <Section title={appState.t("billing.title")}>
                <Row
                    onClick={holder ? () => onSelectOrg(holder.id) : undefined}
                    chevron={!!holder}
                    trailing={<Pill tone="neutral">{appState.t("common.inherited")}</Pill>}
                >
                    <p className="l">
                        {appState.t("billing.coveredBy", { name: holder?.name ?? `${node.billing_organisation_id}` })}
                    </p>
                    <p className="detail-text">
                        {holder
                            ? appState.t("billing.coveredByHint")
                            : appState.t("billing.coveredByUnavailable")}
                    </p>
                </Row>
            </Section>
        );
    }

    return (
        <Section title={appState.t("billing.title")}>
            {canEdit ? (
                <>
                    <Subscription orgId={node.id} canEdit prominent />
                    <BillingContact orgId={node.id} canEdit={canEdit} onChanged={onChanged} />
                </>
            ) : (
                <Row trailing={<Pill tone="warn">{appState.t("billing.unpaid")}</Pill>}>
                    <p className="l">{appState.t("billing.noPaymentPath")}</p>
                    <p className="detail-text">
                        {appState.t("billing.registerHint")}
                    </p>
                </Row>
            )}
        </Section>
    );
});
